import { IVariableBasePrices, IVariableBasePriceOverrides } from "@llws/lift-entity-interfaces";
import {
  AccordionDetails, AccordionSummary, Alert, Button, FormControlLabel, Grid, Stack, Switch, Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { StyledAccordion } from "../../tiered-unit-count/styledComponents";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { appendRange, sortBy } from "@hydra/internal";
import {
  VariableBasePriceTable,
} from "src/components/pages/Subscriptions/EditorComponents/VariableBasePriceEditor/VariableBasePriceTable";
import isEqual from 'lodash/isEqual';
import { floatVal } from "@jamesgmarks/utilities";

interface IVariableBasePriceWithRange extends Partial<IVariableBasePrices> {
  range: string;
}

export interface IVariableBasePriceOverrideWithRange extends Partial<IVariableBasePriceOverrides> {
  range: string;
}

export interface IVBPData {
  rangedVBP: IVariableBasePriceWithRange[];
  rangedVBPO: IVariableBasePriceOverrideWithRange[];
}

interface IVariableBasePriceEditorProps {
  rangedVBP: IVariableBasePriceWithRange[];
  rangedVBPO: IVariableBasePriceOverrideWithRange[];
  defaultVBPData: IVBPData;
  onSave: (updatedVBPO: IVariableBasePriceOverrideWithRange[]) => void;
  disabled?: boolean;
  hasErrors?: boolean;
}

export const isVBPOChanged = (
  current: Partial<IVariableBasePriceOverrides>,
  previous?: IVariableBasePriceOverrides,
) => {
  if (!previous) {
    return true;
  }

  const fieldsToCompare: Array<keyof IVariableBasePriceOverrides> = [
    'basePrice',
    'costPerUnit',
    'maxValue',
  ];

  return fieldsToCompare.some(field => {
    const currentValue = current[field];
    const previousValue = previous[field];

    const currentNum = currentValue === null
      ? null
      : (typeof currentValue === 'string' || typeof currentValue === 'number')
        ? floatVal(`${currentValue}`)
        : currentValue;
    const previousNum = previousValue === null
      ? null
      : (typeof previousValue === 'string' || typeof previousValue === 'number')
        ? floatVal(`${previousValue}`)
        : previousValue;

    return currentNum !== previousNum;
  });
};

export const VariableBasePriceEditor: React.FC<IVariableBasePriceEditorProps> = ({
  rangedVBP,
  rangedVBPO,
  defaultVBPData,
  onSave,
  disabled = false,
  hasErrors = false,
}) => {
  const [draftVBPO, setDraftVBPO] = useState<IVariableBasePriceOverrideWithRange[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [useDefault, setUseDefault] = useState(rangedVBPO.length === 0);
  const [hasChangeBeenMade, setHasChangeBeenMade] = useState(false);

  useEffect(() => {
    if (rangedVBPO) {
      setDraftVBPO(
        rangedVBPO.map((vbpo) => ({
          ...vbpo,
        })),
      );
    }
  }, [rangedVBPO]);

  useEffect(() => {
    setHasChangeBeenMade(!isEqual(draftVBPO, rangedVBPO) || (useDefault && rangedVBPO.length > 0));
  }, [draftVBPO, rangedVBPO, useDefault]);

  const handleUpdate = (index: number, key: keyof IVariableBasePriceOverrideWithRange, value: number | null) => {
    setDraftVBPO((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
  };

  const handleVBPSave = (defaultValue: boolean = false) => {
    setUseDefault(defaultValue);
    if (defaultValue) {
      onSave([]);
      setIsEditing(false);
      return;
    }
    const vbpoToUse = draftVBPO.length === 0 ? defaultVBPData.rangedVBPO : draftVBPO;
    const sortedVBPO = sortBy(vbpoToUse, 'maxValue');
    const variableBasePriceOverride = appendRange(sortedVBPO, 'maxValue');
    setDraftVBPO(variableBasePriceOverride);

    onSave(variableBasePriceOverride);
    setIsEditing(false);
  };

  const addOverride = () => {
    setDraftVBPO((prev) =>
      [...prev, { range: '', maxValue: null, basePrice: 0, costPerUnit: 0 }]);
  };

  const overrideErrors = useMemo(() => {
    if (useDefault) {
      return [];
    }
    const nullItems = draftVBPO.filter((item) => item.maxValue === null);
    const numberOfNulls = nullItems.length;
    return [
      ...(
        numberOfNulls > 1
          ? ['Only one range can be left blank']
          : []
      ),
      ...(
        numberOfNulls === 0 && draftVBPO.length !== 0
          ? ['Must have exactly one range as blank']
          : []
      ),
    ];
  }, [draftVBPO, useDefault]);

  return (
    <StyledAccordion
      hasErrors={overrideErrors.length > 0 || hasErrors}
      disabled={disabled}
      expanded={expanded && !disabled}
      onChange={() => setExpanded((old) => !old)}
    >
      <AccordionSummary expandIcon={<GridExpandMoreIcon sx={{ fontSize: '2rem' }} />}>
        <Typography variant="h6">Manage price brackets...</Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Stack direction="column" justifyContent="space-between">
          <Stack direction="row" justifyContent="space-between">
            <FormControlLabel
              control={
                <Switch
                  checked={useDefault}
                  onChange={() => {

                    handleVBPSave(!useDefault);
                  }}
                  color="primary"
                />
              }
              label="Use default values"
            />
            {
              !useDefault
              && <Button
                variant="text"
                size="small"
                color="secondary"
                onClick={() => {
                  setDraftVBPO(defaultVBPData.rangedVBPO);
                }}
              >
                Reset Values
              </Button>
            }
          </Stack>

          {
            overrideErrors.map((errorMessage) => (
              <Grid key={errorMessage} item xs={12}>
                <Alert icon={false} color="warning">{errorMessage}</Alert>
              </Grid>
            ))
          }

          <VariableBasePriceTable
            rows={useDefault ? rangedVBP : draftVBPO}
            onChange={(useDefault ? null : handleUpdate)}
            readOnly={!isEditing}
          />
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2}>
          <div>
            <Button
              variant="contained"
              color="warning"
              onClick={() => setIsEditing((prev) => {
                if (prev) {
                  setDraftVBPO(rangedVBPO);
                }
                return !prev;
              })}
              sx={{ mt: 2, mr: 2 }}
              disabled={useDefault}
            >
              {isEditing ? 'Cancel' : 'Edit'}
            </Button>
            {isEditing && <Button
              variant="contained"
              color="primary"
              onClick={() => handleVBPSave()}
              sx={{ mt: 2 }}
              disabled={!hasChangeBeenMade || overrideErrors.length > 0}
            >
              Save
            </Button>
            }
          </div>
          {
            isEditing
            && <Button
              variant="text"
              color="primary"
              onClick={() => addOverride()}
              sx={{ mt: 2 }}
              disabled={useDefault}
            >
              Add new row
            </Button>
          }
        </Stack>
      </AccordionDetails>
    </StyledAccordion >);
};
